/* eslint-disable no-undef */
/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/**
 * This component is responsible for rendering the selected tail's flight path on the map (where the plane has flown
 * since takeoff)
 *
 * It uses the "selectedTerminalFlightPath" from the redux store to draw the flight path.
 *
 * Flight paths consists of an array of objects that have a sourceTime, latitude, and longitude, and platformConnectivityStatus.
 * The sourceTime is the time at which the terminal reported its location (a VMT Periodic Message). These segments of online
 * activity are rendered green.
 *
 * Ideally there a terminal will send its VMT Periodic Message every minute. There are cases where a message will not
 * send but the terminal is online (banking for example can cause this).
 *
 * If the there is a gap greater than or equal than 2 minutes (this is the interval that the NMSPM uses) between
 * waypoints in the flight path, then that constitutes a period of offline activity and that segment is rendered as a
 * grey line.
 */

import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { groupWith } from 'ramda';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import size from 'lodash/size';
import { withTheme } from 'styled-components';
import { RootState } from '../../../store';
import { FlightPathWaypoint } from '../../../shared/entities/terminal';
import { minutesBetweenDates } from '../../../utils/DateTimeUtils';
import { isStandby } from '../../../utils/ConnectionStatusUtils';
import StyledProps from '../../common/theme/AppThemeInterface';
import { ResourceFilter } from '../../common/table/types';
import MenuItems from '../../common/layout/menu/MenuItems';
import { PortalsEnum } from '../../../shared/entities/portal';
// If there is a gap between flight path waypoints greater than two minutes then that constitutes a period of offline
// activity. This value of 2 minutes is used in the NMSPM
// Temporarily updated to 4 minutes to match the other charts
const OFFLINE_THRESHOLD_MINUTES = 4;
const GEODESIC = false;

// A flight path segment is a list of flight path waypoints (grouped together if consecutive waypoints have source times
// less than 2 minutes apart
export interface FlightPathSegment {
  flightPathWaypoints: FlightPathWaypoint[];
}

// Get the google map instance provided by the AircraftFleetMapContainer context
export interface FlightPathContainerProps extends StateProps, StyledProps {
  map: google.maps.Map;
}

function FlightPathContainer({
  map,
  selectedTerminalFlightPath,
  selectedDatapathUuidFleetView,
  tailDetailsPanelTerminal,
  fleetViewFilters,
  fleetViewHiddenDatapathUuids,
  theme,
}: FlightPathContainerProps) {
  // The flightPathSegmentPolylines is the array of Polylines that are linked up to form one multicolored path
  // representing where the plane has flown since takeoff
  const [flightPathSegmentPolylines, setFlightPathSegmentPolylines] = React.useState([] as google.maps.Polyline[]);
  const [shouldRenderFlightpath, setShouldRenderFlightpath] = React.useState(false);
  const isFtvView = useRouteMatch(`/${MenuItems.fleet}/:datapathUuid`);

  // Process the flight path waypoints (represented as an array of: {sourceTime, latitudeDeg, longitudeDeg}) and create
  // an array of arrays where each array is a group of online waypoints with less than 2 minutes between their
  // consecutive source times.
  //
  // The last element in one array will have a source time 2 minutes or older than the first element in the array that
  // follows it
  const getOnlineSegments = useCallback((flightPathWaypoints: FlightPathWaypoint[]): FlightPathSegment[] => {
    return groupWith((a: FlightPathWaypoint, b: FlightPathWaypoint) => {
      const minutes = minutesBetweenDates(a.sourceTime, b.sourceTime);
      return minutes < OFFLINE_THRESHOLD_MINUTES;
    }, flightPathWaypoints).map((segmentWaypoint: FlightPathWaypoint) => ({
      flightPathWaypoints: segmentWaypoint,
    }));
  }, []);

  // Construct a Google maps polyline given an array of waypoints, the color of the line,
  // and whether or not there should be a takeoff indicator at the beginning of the polyline.
  const constructPolyline = useCallback(
    (waypoints: FlightPathWaypoint[], color: string, hasTakeoffIndicator: boolean = false) => {
      const polyline = new google.maps.Polyline({
        path: waypoints.map((flightPathWaypoint: FlightPathWaypoint) => ({
          lat: flightPathWaypoint.latitudeDeg,
          lng: flightPathWaypoint.longitudeDeg,
        })),
        geodesic: GEODESIC, // Don't render the path as geodesic to represent the closest point between two points on a sphere
        strokeColor: color,
        strokeOpacity: 1.0,
        strokeWeight: theme.flightPathStrokeWeight,
        zIndex: 1,
      });
      if (hasTakeoffIndicator) {
        polyline.setOptions({
          icons: [
            {
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: color,
                scale: 3,
              },
              offset: '0%',
            },
          ],
        });
      }
      return polyline;
    },
    [theme.flightPathStrokeWeight],
  );

  // Remove all of the flight path Polylines from the map
  const removePolylines = useCallback((flightPathSegmentPolylines: google.maps.Polyline[]) => {
    flightPathSegmentPolylines.forEach((polyline: google.maps.Polyline) => {
      polyline.setMap(null);
    });
  }, []);

  // Take in the processed flight path waypoints (an array of arrays of online segments) and render the corresponding
  // Polylines on the map
  const drawPolylines = useCallback(
    (polylines: google.maps.Polyline[]): void => {
      polylines.forEach((polyline: google.maps.Polyline) => polyline.setMap(map));
    },
    [map],
  );

  // Determine whether flightpath should be rendered or not
  useEffect(() => {
    let shouldRender = true;
    if (!isNil(tailDetailsPanelTerminal) && !isNil(selectedDatapathUuidFleetView)) {
      if (!isFtvView && !isNil(fleetViewFilters)) {
        fleetViewFilters.forEach((fleetViewFilter: ResourceFilter) => {
          if (get(tailDetailsPanelTerminal, fleetViewFilter.key) !== fleetViewFilter.value) {
            shouldRender = false;
          }
        });
      }

      if (!isFtvView && shouldRender && size(fleetViewHiddenDatapathUuids)) {
        if (fleetViewHiddenDatapathUuids.includes(selectedDatapathUuidFleetView)) {
          shouldRender = false;
        }
      }
    } else {
      shouldRender = false;
    }
    setShouldRenderFlightpath(shouldRender);
  }, [
    fleetViewFilters,
    tailDetailsPanelTerminal,
    selectedDatapathUuidFleetView,
    setShouldRenderFlightpath,
    flightPathSegmentPolylines,
    isFtvView,
    fleetViewHiddenDatapathUuids,
  ]);

  // Update Google polyline array that will be drawn on the map
  useEffect(() => {
    const validLatLng = (waypoint: FlightPathWaypoint) => !isNil(waypoint.latitudeDeg) && !isNil(waypoint.longitudeDeg);
    if (isNil(selectedTerminalFlightPath) || isNil(selectedTerminalFlightPath.find(validLatLng))) {
      return;
    }

    const terminalFlightPath = selectedTerminalFlightPath.filter(validLatLng);

    // Create a temporary array of Polylines which will be populated with
    // online segments and offline segments between the online segments
    const polylines: google.maps.Polyline[] = [];

    // Create Polylines for the online segments
    const onlineSegments = getOnlineSegments(terminalFlightPath);
    onlineSegments.forEach((segment: FlightPathSegment, index: number) => {
      // Split online segment into active and standby segments
      const activeWaypoints = [] as FlightPathWaypoint[];
      const standbyWaypoints = [] as FlightPathWaypoint[];
      segment.flightPathWaypoints.forEach((waypoint: FlightPathWaypoint, waypointIdx: number) => {
        const prevWaypoint = segment.flightPathWaypoints[Math.max(0, waypointIdx - 1)];
        const designatedWaypointArray = isStandby(waypoint.platformConnectivityStatus)
          ? standbyWaypoints
          : activeWaypoints;
        // This will close the gaps between active and standby segments
        if (prevWaypoint.platformConnectivityStatus !== waypoint.platformConnectivityStatus) {
          designatedWaypointArray.push(prevWaypoint);
        }
        designatedWaypointArray.push(waypoint);
      });

      // Split up the non-contiguous segments
      const activeSegments = groupWith(
        (a: FlightPathWaypoint, b: FlightPathWaypoint) =>
          minutesBetweenDates(a.sourceTime, b.sourceTime) < OFFLINE_THRESHOLD_MINUTES,
        activeWaypoints,
      );
      const standbySegments = groupWith(
        (a: FlightPathWaypoint, b: FlightPathWaypoint) =>
          minutesBetweenDates(a.sourceTime, b.sourceTime) < OFFLINE_THRESHOLD_MINUTES,
        standbyWaypoints,
      );

      // Construct polylines
      activeSegments.forEach((waypoints: FlightPathWaypoint[], activeSegementsIdx: number) => {
        if (size(waypoints)) {
          // For the first Polyline draw a green circle representing the takeoff location
          const hasTakeoffIndicator =
            activeSegementsIdx === 0 &&
            index === 0 &&
            !isStandby(segment.flightPathWaypoints[0].platformConnectivityStatus);
          const activeSegmentPolyline = constructPolyline(waypoints, theme.activeStatusColor, hasTakeoffIndicator);
          polylines.push(activeSegmentPolyline);
        }
      });
      standbySegments.forEach((waypoints: FlightPathWaypoint[], standbySegmentsIdx: number) => {
        if (size(waypoints)) {
          // For the first Polyline draw a yellow circle representing the takeoff location
          const hasTakeoffIndicator =
            standbySegmentsIdx === 0 &&
            index === 0 &&
            isStandby(segment.flightPathWaypoints[0].platformConnectivityStatus);
          const standbySegmentPolyline = constructPolyline(waypoints, theme.standbyStatusColor, hasTakeoffIndicator);
          polylines.push(standbySegmentPolyline);
        }
      });

      // Construct the offline segment between online segments
      if (index < onlineSegments.length - 1) {
        const offlineSegmentPolyline = constructPolyline(
          [
            onlineSegments[index].flightPathWaypoints[onlineSegments[index].flightPathWaypoints.length - 1],
            onlineSegments[index + 1].flightPathWaypoints[0],
          ],
          theme.offlineStatusColor,
          false,
        );

        // Add the offline segment to the array of temporary Polylines
        polylines.push(offlineSegmentPolyline);
      }
    });
    // Set the flight path segments to the temporary array of Polylines
    setFlightPathSegmentPolylines(polylines);

    return () => {
      setFlightPathSegmentPolylines([]);
    };
  }, [
    getOnlineSegments,
    constructPolyline,
    setFlightPathSegmentPolylines,
    selectedTerminalFlightPath,
    theme.activeStatusColor,
    theme.standbyStatusColor,
    theme.flightPathStrokeWeight,
    theme.offlineStatusColor,
  ]);

  // Whenever the flightpath receives new properties, render the flight path.
  // Remove polylines upon cleanup.
  useEffect(() => {
    if (shouldRenderFlightpath) {
      drawPolylines(flightPathSegmentPolylines);
    }
    return () => {
      removePolylines(flightPathSegmentPolylines);
    };
  }, [flightPathSegmentPolylines, shouldRenderFlightpath, drawPolylines, removePolylines]);

  return null;
}

const mapStateToProps = (state: RootState) => ({
  selectedTerminalFlightPath: state.terminals.selectedTerminalFlightPath[PortalsEnum.UNIFIED_PORTAL],
  selectedDatapathUuidFleetView:
    state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList,
  tailDetailsPanelTerminal: state.terminals.tailDetailsPanelTerminal,
  fleetViewFilters: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].terminalFilters,
  fleetViewHiddenDatapathUuids: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].hiddenTerminalsList,
});
type StateProps = ReturnType<typeof mapStateToProps>;

export default withTheme(connect(mapStateToProps, null)(FlightPathContainer));
